import { Check, ArrowRight, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

const mockSystems = [
  {
    name: "GPTBot",
    company: "OpenAI",
    color: "#10a37f",
    action: "allow" as const,
    visits: "847",
  },
  {
    name: "ClaudeBot",
    company: "Anthropic",
    color: "#cc785c",
    action: "limit" as const,
    visits: "234",
  },
  {
    name: "PerplexityBot",
    company: "Perplexity",
    color: "#20b2aa",
    action: "block" as const,
    visits: "12",
  },
  {
    name: "Google AI",
    company: "Google",
    color: "#4285f4",
    action: "allow" as const,
    visits: "1,204",
  },
];

const actionStyles = {
  allow: "bg-green-500/20 text-green-400 border-green-500/40",
  limit: "bg-amber-500/20 text-amber-400 border-amber-500/40",
  block: "bg-red-500/20 text-red-400 border-red-500/40",
};

const unselected = "bg-secondary/30 text-muted-foreground border-transparent";

const bullets = [
  "Detect 11 AI systems automatically",
  "Block, allow, or rate-limit per system",
  "Get alerted when new AI finds your site",
];

const AITrafficSection = () => (
  <section id="ai-traffic-section" className="relative py-24 px-6">
    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      {/* Left — Copy */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <span className="inline-block text-[10px] font-mono uppercase tracking-[0.25em] text-primary mb-4">
          AI Traffic Control
        </span>

        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6 leading-[1.1]">
          Know exactly which AI is{" "}
          <span className="text-primary">training on your content.</span>
        </h2>

        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-8 max-w-lg">
          GPTBot, ClaudeBot, PerplexityBot — they're scraping your store right
          now. BotSweep shows you who and gives you full control.
        </p>

        <ul className="space-y-3 mb-10">
          {bullets.map((b) => (
            <li key={b} className="flex items-center gap-3 text-sm text-foreground/90">
              <Check className="w-4 h-4 text-primary shrink-0" />
              {b}
            </li>
          ))}
        </ul>

        <Link to="/auth">
          <Button
            size="lg"
            className="text-xs font-mono bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.1em] h-11 px-8 btn-shimmer"
          >
            See Your AI Traffic
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
        <p className="mt-3 text-[11px] font-mono text-muted-foreground/60">
          Free 14-day trial · No credit card required
        </p>
      </motion.div>

      {/* Right — Product Mockup */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="rounded-xl border bg-card p-5 sm:p-6"
        style={{
          boxShadow: "0 0 40px rgba(0,212,255,0.1)",
          borderColor: "rgba(0,212,255,0.2)",
        }}
      >
        {/* Header */}
        <div className="mb-5">
          <h3 className="text-sm font-semibold text-foreground">
            AI System Controls
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            3 systems detected this week
          </p>
        </div>

        {/* Rows */}
        <div className="space-y-3">
          {mockSystems.map((s) => (
            <div
              key={s.name}
              className="flex items-center gap-3 rounded-lg bg-secondary/20 px-3 py-2.5"
            >
              {/* Dot */}
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0 animate-pulse"
                style={{ backgroundColor: s.color }}
              />

              {/* Name */}
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium text-foreground">
                  {s.name}
                </span>
                <span className="text-[10px] text-muted-foreground ml-1.5">
                  {s.company}
                </span>
              </div>

              {/* Action buttons */}
              <div className="flex gap-1">
                {(["allow", "limit", "block"] as const).map((a) => (
                  <span
                    key={a}
                    className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${
                      s.action === a ? actionStyles[a] : unselected
                    }`}
                  >
                    {a}
                  </span>
                ))}
              </div>

              {/* Visits */}
              <span className="text-[10px] text-muted-foreground whitespace-nowrap hidden sm:inline">
                {s.visits} visits
              </span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <TrendingUp className="w-3 h-3" />
          3 new AI systems detected this month
        </div>
      </motion.div>
    </div>
  </section>
);

export default AITrafficSection;
